import { DownOutlined } from '@ant-design/icons';
import { Menu, type MenuProps, Space, Tree, Typography } from 'antd';
import type { DataNode } from 'antd/lib/tree';
import { isArray } from 'lodash';
import { isEmpty, uniqWith } from 'lodash/fp';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useGetModelConfiguratorFiltersQuery } from '../../../../../api/queries/getModelConfiguratorFilters';
import { type BodyType } from '../../../../../api/types';
import { useThemeComponents } from '../../../../../themes';
import useBodyTypeDescription from '../../../../../utilities/useBodyTypeDescription';
import useTranslatedString from '../../../../../utilities/useTranslatedString';
import { StyledFilterButton } from '../ui';

const StyledMenu = styled(Menu)`
    & .ant-dropdown-menu-item-group-title {
        font-size: 16px;
        font-weight: 700;
        color: rgba(0, 0, 0, 0.85);
    }

    & .ant-dropdown-menu-item:hover,
    .ant-dropdown-menu-item-active {
        background-color: transparent;
    }
`;

export const DropdownLabelContainer = styled.span`
    width: 100%;
`;

export const StyledDropdown = styled(DownOutlined)`
    &.anticon {
        font-size: 10px;
        height: 100%;
        display: flex;
        align-items: center;
    }
`;

export const DropdownButtonContainer = styled.div`
    & .ant-btn {
        padding: 0.6875rem 0.9375rem 0.6875rem 0.9375rem;
    }
`;

export type MenuListProps = {
    engines: string[];
    bodyTypes: BodyType[];
    filter: Filter;
    setFilter: Dispatch<SetStateAction<Filter>>;
};

const MenuList = ({ engines, bodyTypes, filter, setFilter }: MenuListProps) => {
    const { t } = useTranslation(['configuratorListVehicle']);
    const bodyTypeDescription = useBodyTypeDescription();

    const treeData: DataNode[] = useMemo(
        () =>
            [
                bodyTypes.length > 1 && {
                    title: t('configuratorListVehicle:filters.bodyType'),
                    key: t('configuratorListVehicle:filters.bodyType'),
                    children: bodyTypes.map(bodyType => ({
                        title: bodyTypeDescription(bodyType),
                        key: bodyType,
                    })),
                },
                engines.length > 1 && {
                    title: t('configuratorListVehicle:filters.engine'),
                    key: t('configuratorListVehicle:filters.engine'),
                    children: engines.map(engine => ({
                        title: engine,
                        key: engine,
                    })),
                },
            ].filter(Boolean),
        [bodyTypes, engines, t, bodyTypeDescription]
    );

    const onCheck = useCallback(
        (checkedKeys: string[]) => {
            const filterEngines = engines.filter(engine => checkedKeys.includes(engine));
            const filterBodyTypes = bodyTypes.filter(bodyType => checkedKeys.includes(bodyType));

            setFilter({
                engines: isEmpty(filterEngines) ? null : filterEngines,
                bodyTypes: isEmpty(filterBodyTypes) ? null : filterBodyTypes,
            });
        },
        [bodyTypes, engines, setFilter]
    );

    const filterItems: MenuProps['items'] = useMemo(
        () =>
            [
                (bodyTypes.length > 1 || engines.length > 1) && {
                    label: (
                        <Tree
                            expandedKeys={
                                [
                                    t('configuratorListVehicle:filters.bodyType'),
                                    t('configuratorListVehicle:filters.engine'),
                                ] as React.Key[]
                            }
                            onCheck={onCheck}
                            selectable={false}
                            treeData={treeData}
                            checkable
                        />
                    ),
                    key: null,
                },
            ].filter(Boolean),
        [bodyTypes.length, engines.length, treeData, t, onCheck]
    );

    return <StyledMenu items={filterItems} selectable={false} multiple />;
};

export type Filter = {
    engines: string[] | null;
    bodyTypes: BodyType[] | null;
};

export type FilterMenuProps = {
    moduleId: string;
    filter: Filter;
    setFilter: Dispatch<SetStateAction<Filter>>;
};

const FilterMenu = ({ moduleId, filter, setFilter }: FilterMenuProps) => {
    const [openMenu, setOpenMenu] = useState(false);
    const { Button, ConfiguratorDropdown } = useThemeComponents();
    const { t } = useTranslation(['configuratorListVehicle']);
    const tranlsatedString = useTranslatedString();

    const { data } = useGetModelConfiguratorFiltersQuery({
        variables: { moduleId, productionOnly: true },
        fetchPolicy: 'cache-first',
    });

    const { engines, bodyTypes } = useMemo(
        () => ({
            engines: uniqWith(
                (arrVal, othVal) => arrVal.toLowerCase() === othVal.toLowerCase(),
                (data?.configuratorFilters?.engines || []).map(engine => tranlsatedString(engine)).filter(Boolean)
            ),
            bodyTypes: uniqWith(
                (arrVal, othVal) => arrVal.toLowerCase() === othVal.toLowerCase(),
                data?.configuratorFilters?.bodyTypes.filter(Boolean) || []
            ),
        }),
        [data, tranlsatedString]
    );

    const handleOpenChange = useCallback((open: boolean) => {
        setOpenMenu(open);
    }, []);

    const filterAppliedCount = useMemo(() => {
        const enginesCount = isArray(filter.engines) ? filter.engines.length : 0;
        const bodyTypesCount = isArray(filter.bodyTypes) ? filter.bodyTypes.length : 0;

        return enginesCount + bodyTypesCount;
    }, [filter.bodyTypes, filter.engines]);

    if (engines.length <= 1 && bodyTypes.length <= 1) {
        return null;
    }

    return (
        <Space align="center" size={16}>
            <Typography.Text style={{ fontSize: 18 }}>{t('configuratorListVehicle:labels.filterBy')}</Typography.Text>
            <ConfiguratorDropdown
                onOpenChange={handleOpenChange}
                open={openMenu}
                overlay={<MenuList bodyTypes={bodyTypes} engines={engines} filter={filter} setFilter={setFilter} />}
            >
                <DropdownButtonContainer>
                    <Button icon={null} style={{ minWidth: '10rem' }} type="tertiary" block>
                        <StyledFilterButton>
                            <DropdownLabelContainer>
                                {filterAppliedCount > 0
                                    ? t('configuratorListVehicle:actions.filterApplied', { count: filterAppliedCount })
                                    : t('configuratorListVehicle:actions.pleaseSelect')}
                            </DropdownLabelContainer>
                            <StyledDropdown />
                        </StyledFilterButton>
                    </Button>
                </DropdownButtonContainer>
            </ConfiguratorDropdown>
        </Space>
    );
};

export const useFilterMenu = () => {
    const [filter, setFilter] = useState<Filter>({
        engines: null,
        bodyTypes: null,
    });

    return {
        filter,
        render: (moduleId: string) => <FilterMenu filter={filter} moduleId={moduleId} setFilter={setFilter} />,
    };
};

export default FilterMenu;
